import http from "@/helper/http";

const state = {
  // Product mappings
  mappings: [],
  mappingDetails: null,
  suggestions: [],

  // Loading states
  loadingMappings: false,
  loadingMappingDetails: false,
  loadingSuggestions: false,
  creatingMapping: false,
  updatingMapping: false,
  deletingMapping: false,

  // Pagination
  pagination: {
    page: 1,
    per_page: 20,
    total: 0,
    pages: 0,
  },

  // Filters
  filters: {
    other_store_id: null,
    product_ids: null,
    mapping_confidence: null,
    is_active: true,
    q: null,
  },

  // Cache for mappings by store pair (avoid refetching)
  mappingCache: new Map(), // key: "storeId-otherStoreId"

  // Error handling
  error: null,
};

const getters = {
  mappings: (state) => state.mappings,
  mappingDetails: (state) => state.mappingDetails,
  suggestions: (state) => state.suggestions,

  loadingMappings: (state) => state.loadingMappings,
  loadingMappingDetails: (state) => state.loadingMappingDetails,
  loadingSuggestions: (state) => state.loadingSuggestions,
  creatingMapping: (state) => state.creatingMapping,
  updatingMapping: (state) => state.updatingMapping,
  deletingMapping: (state) => state.deletingMapping,

  pagination: (state) => state.pagination,
  filters: (state) => state.filters,
  error: (state) => state.error,

  // Get mapping by ID
  getMappingById: (state) => (id) => {
    return state.mappings.find((m) => m.id === id);
  },

  // Get mappings for a specific store pair (from cache)
  getMappingsForStorePair: (state) => (storeId, otherStoreId) => {
    const key = `${storeId}-${otherStoreId}`;
    return state.mappingCache.get(key) || [];
  },

  // Check if products are mapped
  areProductsMapped: (state) => (productId, otherProductId) => {
    return state.mappings.some(
      (m) =>
        m.product_id === productId &&
        m.other_product_id === otherProductId &&
        m.is_active
    );
  },
};

const mutations = {
  SET_MAPPINGS(state, mappings) {
    state.mappings = mappings;
  },

  SET_MAPPING_DETAILS(state, details) {
    state.mappingDetails = details;
  },

  SET_SUGGESTIONS(state, suggestions) {
    state.suggestions = suggestions;
  },

  SET_LOADING_MAPPINGS(state, loading) {
    state.loadingMappings = loading;
  },

  SET_LOADING_MAPPING_DETAILS(state, loading) {
    state.loadingMappingDetails = loading;
  },

  SET_LOADING_SUGGESTIONS(state, loading) {
    state.loadingSuggestions = loading;
  },

  SET_CREATING_MAPPING(state, creating) {
    state.creatingMapping = creating;
  },

  SET_UPDATING_MAPPING(state, updating) {
    state.updatingMapping = updating;
  },

  SET_DELETING_MAPPING(state, deleting) {
    state.deletingMapping = deleting;
  },

  SET_PAGINATION(state, pagination) {
    state.pagination = { ...state.pagination, ...pagination };
  },

  SET_FILTERS(state, filters) {
    state.filters = { ...state.filters, ...filters };
  },

  // Cache management
  CACHE_MAPPINGS(state, { storeId, otherStoreId, mappings }) {
    const key = `${storeId}-${otherStoreId}`;
    state.mappingCache.set(key, mappings);
  },

  CLEAR_CACHE(state) {
    state.mappingCache.clear();
  },

  ADD_MAPPING(state, mapping) {
    state.mappings.unshift(mapping);
  },

  UPDATE_MAPPING(state, updatedMapping) {
    const index = state.mappings.findIndex((m) => m.id === updatedMapping.id);
    if (index !== -1) {
      state.mappings.splice(index, 1, updatedMapping);
    }
    if (state.mappingDetails?.id === updatedMapping.id) {
      state.mappingDetails = updatedMapping;
    }
  },

  REMOVE_MAPPING(state, mappingId) {
    const index = state.mappings.findIndex((m) => m.id === mappingId);
    if (index !== -1) {
      state.mappings[index].is_active = false;
    }
    if (state.mappingDetails?.id === mappingId) {
      state.mappingDetails.is_active = false;
    }
  },

  SET_ERROR(state, error) {
    state.error = error;
  },

  CLEAR_ERROR(state) {
    state.error = null;
  },
};

const actions = {
  // ==================== FETCH MAPPINGS ====================

  async fetchMappings(
    { commit, state },
    { storeId, filters = {}, page, perPage, useCache = false } = {}
  ) {
    // Check cache first if enabled
    if (useCache && filters.other_store_id) {
      const cached = state.mappingCache.get(
        `${storeId}-${filters.other_store_id}`
      );
      if (cached && cached.length > 0) {
        commit("SET_MAPPINGS", cached);
        return { items: cached };
      }
    }

    commit("SET_LOADING_MAPPINGS", true);
    commit("CLEAR_ERROR");

    try {
      const params = {
        page: page || state.pagination.page,
        per_page: perPage || state.pagination.per_page,
        ...state.filters,
        ...filters,
      };

      const response = await http.get(`/v2/store/${storeId}/product-mappings`, {
        params,
      });

      const data = response.data || {};
      const mappings = data.items || [];

      commit("SET_MAPPINGS", mappings);
      commit("SET_PAGINATION", {
        page: data.page,
        per_page: data.per_page,
        total: data.total,
        pages: data.pages,
      });

      // Cache if filtering by specific store pair
      if (filters.other_store_id) {
        commit("CACHE_MAPPINGS", {
          storeId,
          otherStoreId: filters.other_store_id,
          mappings,
        });
      }

      return data;
    } catch (error) {
      const message =
        error?.response?.data?.error?.message || "Failed to load mappings";
      commit("SET_ERROR", message);
      throw error;
    } finally {
      commit("SET_LOADING_MAPPINGS", false);
    }
  },

  async fetchMappingDetails({ commit }, { storeId, mappingId }) {
    commit("SET_LOADING_MAPPING_DETAILS", true);
    commit("CLEAR_ERROR");

    try {
      const response = await http.get(
        `/v2/store/${storeId}/product-mappings/${mappingId}`
      );
      commit("SET_MAPPING_DETAILS", response.data);
      return response.data;
    } catch (error) {
      const message =
        error?.response?.data?.error?.message ||
        "Failed to load mapping details";
      commit("SET_ERROR", message);
      throw error;
    } finally {
      commit("SET_LOADING_MAPPING_DETAILS", false);
    }
  },

  async fetchSuggestions({ commit }, { storeId, otherStoreId, limit = 25 }) {
    commit("SET_LOADING_SUGGESTIONS", true);
    commit("CLEAR_ERROR");

    try {
      const response = await http.get(
        `/v2/store/${storeId}/product-mappings/suggestions`,
        {
          params: { store_id: otherStoreId, limit },
        }
      );

      const suggestions = response.data?.suggestions || [];
      commit("SET_SUGGESTIONS", suggestions);
      return suggestions;
    } catch (error) {
      const message =
        error?.response?.data?.error?.message || "Failed to load suggestions";
      commit("SET_ERROR", message);
      throw error;
    } finally {
      commit("SET_LOADING_SUGGESTIONS", false);
    }
  },

  // ==================== CREATE/UPDATE/DELETE ====================

  async createMapping({ commit }, { storeId, payload }) {
    commit("SET_CREATING_MAPPING", true);
    commit("CLEAR_ERROR");

    try {
      const response = await http.post(
        `/v2/store/${storeId}/product-mappings`,
        payload
      );

      const mapping = response.data;
      commit("ADD_MAPPING", mapping);

      // Invalidate cache for this store pair
      if (payload.other_store_id) {
        const cacheKey = `${storeId}-${payload.other_store_id}`;
        state.mappingCache.delete(cacheKey);
      }

      return mapping;
    } catch (error) {
      const message =
        error?.response?.data?.error?.message || "Failed to create mapping";
      commit("SET_ERROR", message);
      throw error;
    } finally {
      commit("SET_CREATING_MAPPING", false);
    }
  },

  async updateMapping({ commit }, { storeId, mappingId, payload }) {
    commit("SET_UPDATING_MAPPING", true);
    commit("CLEAR_ERROR");

    try {
      const response = await http.patch(
        `/v2/store/${storeId}/product-mappings/${mappingId}`,
        payload
      );

      const mapping = response.data;
      commit("UPDATE_MAPPING", mapping);
      return mapping;
    } catch (error) {
      const message =
        error?.response?.data?.error?.message || "Failed to update mapping";
      commit("SET_ERROR", message);
      throw error;
    } finally {
      commit("SET_UPDATING_MAPPING", false);
    }
  },

  async deleteMapping({ commit }, { storeId, mappingId }) {
    commit("SET_DELETING_MAPPING", true);
    commit("CLEAR_ERROR");

    try {
      await http.delete(`/v2/store/${storeId}/product-mappings/${mappingId}`);
      commit("REMOVE_MAPPING", mappingId);
      return true;
    } catch (error) {
      const message =
        error?.response?.data?.error?.message || "Failed to delete mapping";
      commit("SET_ERROR", message);
      throw error;
    } finally {
      commit("SET_DELETING_MAPPING", false);
    }
  },

  // ==================== FILTERS & PAGINATION ====================

  setFilters({ commit }, filters) {
    commit("SET_FILTERS", filters);
  },

  setPage({ commit }, page) {
    commit("SET_PAGINATION", { page });
  },

  clearCache({ commit }) {
    commit("CLEAR_CACHE");
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
